/* Approved pricing display model only; native Shopify amounts remain authoritative. */
(() => {
  'use strict';
  const SIZES = Object.freeze(['lite', 'standard', 'double']);
  const MODES = Object.freeze(['monthly_subscription', 'ninety_subscription', 'monthly_onetime', 'ninety_onetime']);
  const RETAIL = Object.freeze({ HE: 89, PG: 79, PAMPERED: 168 });
  // Captured current one-time packs; retained unchanged from exports 4.
  const PACKS = Object.freeze({ HE: Object.freeze({1:89,2:159,3:199,6:379}), PG: Object.freeze({1:79,2:139,3:175,6:319}), PAMPERED: Object.freeze({1:168,2:298,3:374,6:698}) });
  function packQuote(product, units) {
    const dp = Array(units + 1).fill(null);
    dp[0] = { dollars: 0, packs: [] };
    for (let n = 1; n <= units; n += 1) {
      for (const size of [6,3,2,1]) {
        if (size > n || !dp[n-size]) continue;
        const candidate = { dollars: dp[n-size].dollars + PACKS[product][size], packs: [...dp[n-size].packs, size] };
        if (!dp[n] || candidate.dollars < dp[n].dollars || (candidate.dollars === dp[n].dollars && candidate.packs.length < dp[n].packs.length)) dp[n] = candidate;
      }
    }
    return dp[units];
  }
  function wholeBoxPrices(product, q) {
    if (product === 'HE') return { first: q === 1 ? 59 : 40*q+29, renewal: 50*q+19 };
    if (product === 'PG') return { first: 40*q+9, renewal: 50*q+9 };
    return { first: q === 1 ? 99 : 80*q+29, renewal: 100*q+19 };
  }
  function quote({ product, pets, mode, welcomeEligible = true }) {
    if (!Object.hasOwn(RETAIL, product)) throw new Error('Unsupported product');
    if (!Array.isArray(pets) || pets.length < 1 || pets.length > 6 || pets.some(size => !SIZES.includes(size))) throw new Error('Choose one to six valid pet sizes');
    if (!MODES.includes(mode)) throw new Error('Unsupported purchase mode');
    const counts = { lite: 0, standard: 0, double: 0 };
    pets.forEach(size => { counts[size] += 1; });
    const monthlyUnits = counts.lite/2 + counts.standard + 2*counts.double;
    const subscription = mode.endsWith('subscription');
    const long = mode.startsWith('ninety');
    const singleLite = pets.length === 1 && counts.lite === 1;
    // The new household cadence applies to subscriptions. One-time coverage stays as exported.
    const oldOddLiteOneTime = !subscription && pets.length > 1 && counts.lite === pets.length && counts.lite % 2 === 1;
    const cadenceDays = (singleLite || oldOddLiteOneTime) ? (long ? 180 : 60) : (long ? 90 : 30);
    const units = monthlyUnits * cadenceDays / 30;
    const low = Math.floor(units), high = Math.ceil(units);
    let firstCents, renewalCents = null, packs = [];
    if (subscription) {
      const lower = wholeBoxPrices(product, low), upper = wholeBoxPrices(product, high);
      firstCents = (lower.first + upper.first) * 50;
      renewalCents = (lower.renewal + upper.renewal) * 50;
    } else if (pets.length === 1) {
      // Exact current catalog pack, not an optimizer substitution for a single pet.
      firstCents = PACKS[product][high] * 100;
      packs = [high];
    } else {
      const combination = packQuote(product, high);
      firstCents = combination.dollars * 100;
      packs = combination.packs;
    }
    const todayCents = subscription && welcomeEligible === false ? renewalCents : firstCents;
    const benchmarkCents = RETAIL[product] * (subscription ? units : high) * 100;
    return {
      product, mode, pets: [...pets], counts, subscription, welcomeEligible: welcomeEligible !== false,
      monthlyUnits, cadenceDays, units, firstShipmentUnits: high, lowShipmentUnits: subscription ? low : high,
      firstCents, renewalCents, todayCents, benchmarkCents, packs,
      savingsDollars: Math.max(0, Math.floor((benchmarkCents - todayCents) / 100)),
      alternates: subscription && pets.length > 1 && counts.lite % 2 === 1,
      actualCareDays: high * 30 / monthlyUnits,
      sachetsPerFormula: high * 30,
      formulaCount: product === 'PAMPERED' ? 2 : 1
    };
  }
  const money = cents => '$' + (cents / 100).toLocaleString('en-US', { maximumFractionDigits: 2 });
  const ctaLabel = q => q.savingsDollars > 0 ? `Add to Cart · Save $${q.savingsDollars.toLocaleString('en-US')}` : 'Add to Cart';
  function leadPrice(q) {
    const months = q.cadenceDays / 30;
    const roundedMonthly = q.mode.startsWith('ninety');
    return { months, roundedMonthly,
      cents: roundedMonthly ? Math.round(q.todayCents / months / 100) * 100 : Math.round(q.todayCents / months),
      regularCents: roundedMonthly ? Math.round(q.benchmarkCents / months / 100) * 100 : Math.round(q.benchmarkCents / months) };
  }
  const welcomeAmount = q => q.subscription && q.welcomeEligible ? Math.max(0, q.renewalCents - q.todayCents) : 0;
  function priceAccessibleLabel(q) {
    const lead = leadPrice(q);
    const invoice = q.subscription ? `Renews at ${money(q.renewalCents)} every ${q.cadenceDays} days.` : `Buy once for ${money(q.todayCents)}.`;
    if (lead.months === 1) return q.subscription ? `${money(q.todayCents)}. ${invoice}` : invoice;
    const monthly = `${lead.roundedMonthly ? 'Rounded monthly equivalent' : 'Monthly equivalent'} ${money(lead.cents)} per month.`;
    const comparison = lead.regularCents > lead.cents ? ` Regular price ${money(lead.regularCents)} per month.` : '';
    const total = q.mode === 'ninety_subscription' ? ` ${money(q.todayCents)} total.` : '';
    return `${monthly}${comparison}${total} ${invoice}`;
  }
  function optionAccessibleLabel(q) {
    const title = q.subscription ? (q.cadenceDays === 30 ? 'Monthly Subscription' : `${q.cadenceDays}-Day Subscription`) : `${q.cadenceDays}-Day Supply`;
    const percent = q.benchmarkCents > q.todayCents ? Math.round((q.benchmarkCents - q.todayCents) / q.benchmarkCents * 100) : 0;
    const saving = percent > 0 && q.mode !== 'monthly_onetime' ? ` Save ${percent}%.` : '';
    const price = priceAccessibleLabel(q);
    const contents = q.product === 'PAMPERED' ? `${q.sachetsPerFormula} Hollywood Elixir sachets and ${q.sachetsPerFormula} Pet Gala sachets.` : `${q.sachetsPerFormula} ${q.product === 'HE' ? 'Hollywood Elixir' : 'Pet Gala'} sachets.`;
    const welcome = welcomeAmount(q);
    return `${title}.${saving} ${price} ${contents}${welcome > 0 ? ` ${money(welcome)} welcome offer applied.` : ''}${q.alternates ? ' Lite plans ship every other delivery.' : ''}`;
  }
  const api = Object.freeze({ quote, packQuote, ctaLabel, optionAccessibleLabel, priceAccessibleLabel, SIZES, MODES, RETAIL, PACKS, version: 'multipet-2026-10-13' });
  globalThis.LPLMultiPetPricing = api;
})();
